import { Grid } from '@backstage/ui';
import { getResourceTypeColor } from '@internal/backstage-plugin-catalog-devops-common';
import { useCloudResourcesContext } from '../../Providers';
import styles from '../../resources-page.module.css';

// Same palette as the design mockup; categories outside the map fall back to
// the deterministic type color so any new category still gets a stable one.
const CATEGORY_COLORS: Record<string, string> = {
  Compute: '#1f5493',
  Storage: '#7c5cbf',
  Database: '#0d74ce',
  Network: '#0d8a4f',
  'Security & Identity': '#b45309',
  Containers: '#3a7a6a',
  Integration: '#7a6a00',
  'Management & Governance': '#8a3a8a',
  Other: '#a1a1a1',
};

const categoryColor = (category: string) =>
  CATEGORY_COLORS[category] ?? getResourceTypeColor(category);

const FAILED_SEVERITIES = ['Critical', 'High', 'Medium', 'Low'];

/**
 * Auto-filled filter cards, one per category present in the account (or in
 * the current other-filter scope). Counts are faceted — computed against all
 * the other active filters — so cards stay meaningful after a selection.
 * Clicking a card toggles its category filter. Styled after the mockup.
 */
export function ResourceCategoryCards() {
  const {
    facetResources,
    selectedCategories,
    toggleCategory,
    failedRulesByResource,
  } = useCloudResourcesContext();

  const base = facetResources('category');

  const categories = new Map<
    string,
    { resources: number; services: Set<string>; failedChecks: number }
  >();
  for (const resource of base) {
    const entry =
      categories.get(resource.spec.category) ??
      {
        resources: 0,
        services: new Set<string>(),
        failedChecks: 0,
      };
    entry.resources += 1;
    entry.services.add(resource.spec.service);
    const failed = (failedRulesByResource[resource.metadata.name] ?? []).some(
      failedRule => FAILED_SEVERITIES.includes(failedRule.severity),
    );
    if (failed) entry.failedChecks += 1;
    categories.set(resource.spec.category, entry);
  }

  const total = base.length;

  return (
    <Grid.Root columns="12" gap="3">
      {[...categories.entries()]
        .sort(([, a], [, b]) => b.resources - a.resources)
        .map(([category, stats]) => {
          const share =
            total > 0 ? Math.round((stats.resources / total) * 100) : 0;
          const color = categoryColor(category);
          const isSelected = selectedCategories.includes(category);
          return (
            <Grid.Item
              key={category}
              colSpan={{ xs: '12', sm: '6', lg: '3' }}
            >
              <button
                type="button"
                className={`${styles.catCard}${
                  isSelected ? ` ${styles.catCardSelected}` : ''
                }`}
                aria-pressed={isSelected}
                onClick={() => toggleCategory(category)}
              >
                <span className={styles.catHead}>
                  <span className={styles.catName}>
                    <span
                      className={styles.dot}
                      style={{ backgroundColor: color }}
                    />
                    {category}
                  </span>
                  <span className={styles.catRes}>{stats.resources} {stats.resources === 1 ? 'resource' : 'resources'}</span>
                </span>
                <span className={styles.catValue}>
                  {stats.resources}
                  <span>{` · ${share}%`}</span>
                </span>
                <span className={styles.bar}>
                  <span
                    className={styles.barFill}
                    style={{ width: `${share}%`, backgroundColor: color }}
                  />
                </span>
                <span className={styles.catFoot}>
                  <span>
                    {stats.services.size}{' '}
                    {stats.services.size === 1 ? 'service' : 'services'}
                  </span>
                  <span
                    className={
                      stats.failedChecks > 0 ? styles.catFootDanger : undefined
                    }
                  >
                    {stats.failedChecks} failed{' '}
                    {stats.failedChecks === 1 ? 'check' : 'checks'}
                  </span>
                </span>
              </button>
            </Grid.Item>
          );
        })}
    </Grid.Root>
  );
}
